import { Link, createFileRoute } from "@tanstack/react-router";

import { CopyButton } from "~/components/copy-button";
import { PageHeader } from "~/components/page-header";
import { PaginationBar } from "~/components/pagination-bar";
import { StatusBadge } from "~/components/status-badge";
import { TableEmpty, TableError, TableSkeleton } from "~/components/table-states";
import { Badge } from "~/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { errorMessage } from "~/lib/api";
import { EM_DASH, formatDateTime, formatIDR, orDash } from "~/lib/format";
import { useTransactions } from "~/lib/queries";
import { TransactionFilterBar } from "./-filter-bar";
import {
  hasActiveFilters,
  toFilters,
  validateTransactionSearch,
  type TransactionSearch,
} from "./-search";

export const Route = createFileRoute("/_authed/transactions/")({
  validateSearch: validateTransactionSearch,
  component: TransactionsPage,
});

const COLUMNS = 8;

/**
 * The screen ops lands on. Someone says "order ABC123 is stuck" and the answer
 * starts here.
 *
 * Everything is server-side: the filters, the sort and the paging all happen in
 * Postgres against the indexes migration 013 added. The page this replaces
 * fetched a thousand rows and sorted them in the browser, which stops working
 * at exactly the size where an ops table starts to matter.
 */
function TransactionsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const filters = toFilters(search);
  const { data, error, isPending, isFetching, refetch } = useTransactions(filters);

  /** Patches the URL, resetting to the first page whenever the filters move. */
  function patch(next: Partial<TransactionSearch>) {
    void navigate({
      search: (previous) => ({
        ...previous,
        ...next,
        offset: "offset" in next ? next.offset : 0,
      }),
      replace: true,
    });
  }

  /** Drops every filter and returns to page one, keeping the chosen page size:
   *  that is a display preference, not a filter. */
  function clearFilters() {
    void navigate({ search: (previous) => ({ limit: previous.limit }), replace: true });
  }

  const rows = data?.items ?? [];
  const filtered = hasActiveFilters(search);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Transactions"
        description="Every payment across every storefront, newest first."
      />

      <TransactionFilterBar
        search={search}
        onChange={patch}
        onClear={clearFilters}
        hasFilters={filtered}
      />

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Order ID</TableHead>
              <TableHead>Storefront</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Expires</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {isPending ? (
              <TableSkeleton columns={COLUMNS} />
            ) : error ? (
              <TableError
                columns={COLUMNS}
                message={errorMessage(error)}
                onRetry={() => void refetch()}
              />
            ) : rows.length === 0 ? (
              <TableEmpty
                columns={COLUMNS}
                message={
                  filtered
                    ? "No transactions match these filters."
                    : "No transactions yet."
                }
                onClear={filtered ? clearFilters : undefined}
              />
            ) : (
              rows.map((row) => (
                <TableRow key={row.order_id}>
                  <TableCell>
                    <span className="flex items-center gap-1">
                      <Link
                        to="/transactions/$orderId"
                        params={{ orderId: row.order_id }}
                        className="font-mono text-[0.8125rem] text-primary hover:underline"
                      >
                        {row.order_id}
                      </Link>
                      <CopyButton value={row.order_id} label="order id" />
                      {row.is_deleted ? (
                        <Badge variant="outline" className="ml-1 text-muted-foreground">
                          deleted
                        </Badge>
                      ) : null}
                    </span>
                  </TableCell>

                  {/* The join is a LEFT JOIN: transactions.storefront_id is ON
                      DELETE SET NULL, so a row with no storefront is real and
                      must still be findable. */}
                  <TableCell className="max-w-[14rem] truncate">
                    {row.storefront_name ? (
                      <Link
                        to="/storefronts/$storefrontId"
                        params={{ storefrontId: row.storefront_id }}
                        className="hover:underline"
                      >
                        {row.storefront_name}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">{EM_DASH}</span>
                    )}
                  </TableCell>

                  <TableCell className="text-right tabular-nums">
                    {formatIDR(row.amount)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{row.payment_method}</TableCell>
                  <TableCell>
                    <StatusBadge status={row.payment_status} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">{orDash(row.source)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(row.created_at)}
                  </TableCell>
                  {/* Null for every v1-created and pre-migration-010 row. */}
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(row.expires_at)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {data ? (
          <PaginationBar
            pagination={data.pagination}
            onChange={(next) => patch(next)}
            disabled={isFetching}
          />
        ) : null}
      </div>
    </div>
  );
}
